import React, { useState, useEffect } from 'react'
import PropTypes from 'prop-types'
import MaterialTable from 'material-table'
import { makeStyles } from '@material-ui/styles'
import { get, range } from 'lodash'
import Select from 'react-select'
import Dialog from '../../components/Dialog'
import EmployeesColumnsSelect from './EmployeesColumnsSelect'
import { hasPermissions } from '../../helpers/AccessControl/AccessControl'
import getEmployeeTableColumns from './EmployeeTableColumns'
import LayersClearIcon from '@material-ui/icons/LayersClear';

const customStyles = {
	menu: (provided, state) => ({
		...provided,
		zIndex: 999999999
	}),
}

const useStyles = makeStyles({
	employeesTable: {
		'& table:first-child': {
			'& tr': {
				'& td:first-child, th:first-child': {
					backgroundColor: '#f5f5f5',
					position: 'sticky',
					left: 0,
					zIndex: 100,
				},
				'& th:first-child': {
					zIndex: 1001,
				},
			},
			'& tbody': {
				'& tr:first-child': {
					'& td': {
						padding: 0,
						position: 'sticky',
						top: 55,
						'& .MuiInput-formControl': {
							marginTop: 0,
						},
						'& .MuiIcon-root': {
							fontSize: '.8rem',
						},
						'& .MuiInputBase-input': {
							fontSize: '.7rem',
						},
						'& .MuiSelect-select': {
							padding: '3.7px',
						}
					},
				},
			},
		},
	},
})

export default function EmployeesTable(props) {
	const classes = useStyles()

	// Start: pre columns formation work
	let jobLevels = {}
	range(0, 11).forEach(i => {
		jobLevels[i] = i.toString()
	})
	const jobLevelsSelectableValues = []
	for (const jobLevelsKey in jobLevels) {
		jobLevelsSelectableValues.push({ value: jobLevelsKey, label: jobLevels[jobLevelsKey] })
	}

	const levels = {
		'E': ['6', '7', '8', '9', '10'],
		'M': ['1', '2', '3', '4', '5', '6'],
		'P': ['1', '2', '3', '4', '5', '6', '7', '8'],
		'S': ['1', '2', '3', '4', '5', '6'],
	}

	const categoryLabels = {
		E: 'Executive',
		M: 'People Mgmt',
		P: 'Professional',
		S: 'Support',
	}

	const jobCategoriesSelectableValues = []
	for (const categoryLabelKey in categoryLabels) {
		jobCategoriesSelectableValues.push({ value: categoryLabelKey, label: categoryLabels[categoryLabelKey] })
	}


	const jobCategoryChanged = (props, category) => {
		props.onChange(category.value)
	}
	const jobLevelChanged = (props, level) => {
		props.onChange(level.value)
	}

	const findLevelsByCategory = (category) => {
		let categoryLevel = []
		if (category) {
			categoryLevel = levels[category]
		}
		return categoryLevel
	}

	const jobClassificationEditComponent = props => {
		return (
			<Select
				value={{ value: props.value, label: categoryLabels[props.value] }}
				onChange={(selectedCategory) => jobCategoryChanged(props, selectedCategory)}
				options={jobCategoriesSelectableValues}
				styles={customStyles}
			/>
		)
	}

	const jobLevelEditComponent = props => {
		const selectableLevels = findLevelsByCategory(props.rowData.compkit_job_category).map(level => { return { value: level, label: level } })
		return (
			<Select
				value={{ value: props.value, label: props.value }}
				onChange={(selectedLevel) => jobLevelChanged(props, selectedLevel)}
				options={selectableLevels}
				styles={customStyles}
			/>
		)
	}

	const columnsDefinitions = getEmployeeTableColumns(jobClassificationEditComponent, jobLevelEditComponent, categoryLabels, jobLevels)

	const locallyStoredColumnsMetaStr = localStorage.getItem('employeeColumnsMeta')
	if(!locallyStoredColumnsMetaStr) {
		const employeeColumnsMeta = columnsDefinitions.map(cd => {
			return {
				hidden: false,
				defaultFilter: '',
			}
		})
		localStorage.setItem('employeeColumnsMeta', JSON.stringify(employeeColumnsMeta))
	} else {
		const locallyStoredColumnsMeta = JSON.parse(locallyStoredColumnsMetaStr)
		locallyStoredColumnsMeta.forEach((columnMeta, index) => {
			columnsDefinitions[index].hidden = columnMeta.hidden
			columnsDefinitions[index].defaultFilter = columnMeta.defaultFilter
		})
	}

	const locallyStoredSearchField = localStorage.getItem('employeeSearchField')
	// End

	const [columns, setColumns] = useState(columnsDefinitions)
	const [openEmployeesColumnDlg, setOpenEmployeesColumnDlg] = useState(false)

	useEffect(() => {
		// whenever there is change in columns, persist it in local storage
		const employeeColumnsMeta = columns.map(column => {
			return {
				hidden: column.hidden,
				defaultFilter: column.defaultFilter,
			}
		})
		localStorage.setItem('employeeColumnsMeta', JSON.stringify(employeeColumnsMeta))
	}, [columns])


	const editable = {}

	if(hasPermissions(['create_employees'])){
		editable['onRowAdd'] = (newData) =>
		new Promise(async (resolve, _reject) => {
			let status = await props.createEmployee(newData, columns)
			if(status){
				resolve()
			}else{
				_reject()
			}
		})
	}
	if(hasPermissions(['update_employees'])){
		editable['onRowUpdate'] = (newData, oldData) =>
		new Promise(async (resolve, _reject) => {
			let status = await props.updateEmployee(newData, columns)
			if(status){
				resolve()
			}else{
				_reject()
			}
		})
	}
	if(hasPermissions(['delete_employees'])){
		editable['onRowDelete'] = (employee) =>
		new Promise(async resolve => {
			await props.deleteEmployee(employee)
			resolve()
		})
	}

	const toggleSingleColumnVisibility = (columnIndex, hideShowFlag) => {
		let updateColumns = columns.map((column, index) => {
			if(columnIndex === index) {
				return {
					...column,
					defaultFilter: '',
					hidden: !hideShowFlag,
					tableData: {
						filterValue: ''
					}
				}
			}
			else {
				return {
					...column,
				}
			}
		})
		setColumns(updateColumns)

	}

	const toggleAllColumns = (hideShowFlag) => {
		let updateColumns = columns.map((column, index) => {
			if(hideShowFlag) {
				return {
					...column,
					defaultFilter: '',
					hidden: hideShowFlag,
					tableData: {
						filterValue: ''
					}
				}
			}
			else {
				return {
					...column,
					hidden: hideShowFlag,
				}
			}
		})
		setColumns(updateColumns)
	}

	const clearFilters = () => {
		let updateColumns = columns.map((column, index) => {
			return {
				...column,
				defaultFilter: '',
				tableData: {
					filterValue: ''
				}
			}
		})
		setColumns(updateColumns)
	}

	const persistSearch = props => {
		const employeeSearchField = props
		localStorage.setItem('employeeSearchField', employeeSearchField)
	}

	const persistFilters = (filters) => {
		const columnsUpdatedCopy = [...columns]
		if(filters.length === 0) {
			columnsUpdatedCopy.forEach((_col, index) => {
				columnsUpdatedCopy[index].defaultFilter = ''
			})
		} else {
			filters.forEach((filter) => {
				const columnIndex = filter.column.tableData.columnOrder
				columnsUpdatedCopy[columnIndex].defaultFilter = filter.value
			})
		}
		setColumns(columnsUpdatedCopy)
	}

	const showDialog = () => {
		setOpenEmployeesColumnDlg(true)
	}

	const closeDialog = () => {
		setOpenEmployeesColumnDlg(false)
	}

	const count = get(props.employeesData, 'data.length', 0)
	const title = count === 0 ? 'Employees' : `Employees (${count})`

	return (
		<div className={classes.employeesTable}>
			<Dialog
				isOpen={openEmployeesColumnDlg}
				onClose={closeDialog}
				title={'Employees Column'}
				size={'sm'}
				actions={[
					{
						title: 'Close',
						func: () => closeDialog()
					}
				]}
			>
				<EmployeesColumnsSelect
					columns={columns}
					onColumnHiddenChange={toggleSingleColumnVisibility}
					showAllColumns={() => toggleAllColumns(false)}
					hideAllColumns={() => toggleAllColumns(true)}
				/>
			</Dialog>
			<MaterialTable
				title={title}
				columns={hasPermissions(['read_employees']) && columns}
				data={props.employeesData.data}
				tableRef={props=>{
					if(props){
						const copyProps={...props}
						return copyProps.state.columns = [...columns]
					}
				}}
				options={{
					maxBodyHeight: '600px',
					addRowPosition: 'first',
					headerStyle: {
						backgroundColor: '#D8E4E4',
						color: 'black',
						borderRight: '1px solid #d6cbcb',
						position: 'sticky',
						top: 0,
						whiteSpace: 'nowrap',
					},
					filterCellStyle: {
						backgroundColor: '#f5f5f5',
					},
					pageSize: 50,
					pageSizeOptions: [5, 10, 20, 50],
					filtering: true,
					sorting: true,
					doubleHorizontalScroll: true,
					searchText: locallyStoredSearchField
				}}
				localization={{
					body: {
						addTooltip: 'Add New Employee'
					}
				}}
				actions={[
					{
						icon: 'view_column',
						tooltip: 'Manage Columns',
						isFreeAction: true,
						onClick: (event) => {showDialog()}
					},
					{
						icon: ()=><LayersClearIcon/>,
						tooltip: 'Clear All Filters',
						isFreeAction: true,
						onClick: (event) => {clearFilters()}
					}
				]}
				editable={editable}
				onFilterChange={persistFilters}
				onSearchChange={persistSearch}
			/>
		</div>
	)
}

EmployeesTable.propTypes = {
	employeesData: PropTypes.object.isRequired,
	updateEmployee: PropTypes.func.isRequired,
	deleteEmployee: PropTypes.func.isRequired,
}

EmployeesTable.defaultProps = {
	employeesData: {
		data: [],
		total: 0,
		perPage: 30,
		page: 1,
		lastPage: 0,
	},
}
